import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useAuth } from "../../context/AuthContext";

export const SettingsProfileSection: React.FC = () => {
  const { user, updateUserProfile } = useAuth();

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: "",
    asal_sekolah: "",
    role: "",
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>(
    {}
  );
  const [hasUnsavedProfileChanges, setHasUnsavedProfileChanges] =
    useState(false);

  // Initialize profile data when user is available
  useEffect(() => {
    if (user?.profile) {
      setProfileData({
        name: user.profile.name || "",
        asal_sekolah: user.profile.asal_sekolah || "",
        role: user.profile.role || "",
      });
    }
  }, [user]);

  // Monitor profile changes
  useEffect(() => {
    if (!user?.profile) return;

    const profileChanged =
      profileData.name !== (user.profile.name || "") ||
      profileData.asal_sekolah !== (user.profile.asal_sekolah || "") ||
      profileData.role !== (user.profile.role || "");

    setHasUnsavedProfileChanges(profileChanged);
  }, [profileData, user?.profile]);

  // Handle profile form submission (profile data only)
  const handleSubmitProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate profile form
    const newErrors: Record<string, string> = {};
    if (!profileData.name.trim()) newErrors.name = "Nama harus diisi";
    if (!profileData.asal_sekolah.trim())
      newErrors.asal_sekolah = "Asal sekolah harus diisi";
    if (!profileData.role.trim()) newErrors.role = "Role harus diisi";

    setProfileErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (!user?.id) {
      setProfileErrors({ submit: "User tidak ditemukan" });
      return;
    }

    setIsUpdatingProfile(true);

    try {
      // Update profile data only, preserve existing avatar
      const result = await updateUserProfile(user.id, {
        ...profileData,
        avatar_url: user.profile?.avatar_url || "/avatars/ava1.png",
      });

      if (!result.success && result.error) {
        setProfileErrors({ submit: result.error });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      setProfileErrors({ submit: errorMessage });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle profile input changes
  const handleProfileInputChange = (
    field: keyof typeof profileData,
    value: string
  ) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    if (profileErrors[field]) {
      setProfileErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
      <h3 className="text-lg sm:text-xl font-semibold text-[#373e46] mb-4">
        Edit Profil
      </h3>
      <form onSubmit={handleSubmitProfile} className="space-y-4">
        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Nama
          </label>
          <Input
            id="name"
            type="text"
            value={profileData.name}
            onChange={e => handleProfileInputChange("name", e.target.value)}
            className={`h-10 sm:h-12 rounded-xl border-2 ${
              profileErrors.name ? "border-red-500" : "border-[#e3e6eb]"
            } focus:border-[#6b2cfe] transition-colors`}
            placeholder="Masukkan nama"
          />
          {profileErrors.name && (
            <p className="mt-1 text-sm text-red-500">{profileErrors.name}</p>
          )}
        </div>

        {/* School Field */}
        <div>
          <label
            htmlFor="asal_sekolah"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Asal Sekolah
          </label>
          <Input
            id="asal_sekolah"
            type="text"
            value={profileData.asal_sekolah}
            onChange={e =>
              handleProfileInputChange("asal_sekolah", e.target.value)
            }
            className={`h-10 sm:h-12 rounded-xl border-2 ${
              profileErrors.asal_sekolah ? "border-red-500" : "border-[#e3e6eb]"
            } focus:border-[#6b2cfe] transition-colors`}
            placeholder="Masukkan asal sekolah"
          />
          {profileErrors.asal_sekolah && (
            <p className="mt-1 text-sm text-red-500">
              {profileErrors.asal_sekolah}
            </p>
          )}
        </div>

        {/* Role Field */}
        <div>
          <label
            htmlFor="role"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Role/Mata Pelajaran
          </label>
          <Input
            id="role"
            type="text"
            value={profileData.role}
            onChange={e => handleProfileInputChange("role", e.target.value)}
            className={`h-10 sm:h-12 rounded-xl border-2 ${
              profileErrors.role ? "border-red-500" : "border-[#e3e6eb]"
            } focus:border-[#6b2cfe] transition-colors`}
            placeholder="Masukkan role atau mata pelajaran"
          />
          {profileErrors.role && (
            <p className="mt-1 text-sm text-red-500">{profileErrors.role}</p>
          )}
        </div>

        {/* Email Field (Read-only) */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={user?.email || ""}
            disabled
            className="h-10 sm:h-12 rounded-xl border-2 border-gray-200 bg-gray-100 text-gray-500"
          />
          <p className="mt-1 text-xs text-gray-500">Email tidak dapat diubah</p>
        </div>

        {/* Submit Error */}
        {profileErrors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{profileErrors.submit}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isUpdatingProfile || !hasUnsavedProfileChanges}
          className={`w-full h-10 sm:h-12 font-semibold rounded-xl transition-all duration-300 hover:shadow-lg ${
            hasUnsavedProfileChanges && !isUpdatingProfile
              ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isUpdatingProfile ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Menyimpan...
            </div>
          ) : (
            "Simpan Perubahan"
          )}
        </Button>
      </form>
    </div>
  );
};
